import React, { useRef, useState } from "react";
import emailjs from "@emailjs/browser";
import { ContactForm } from "./ContactForm";

// Toast Component with TypeScript
interface ToastProps {
  message: string;
  type?: "success" | "error";
  onClose: () => void;
}

// Toast Component - Put this ABOVE your main component
function Toast({ message, type = "success", onClose }: ToastProps) {
  // Added ": ToastProps" here
  return (
    <div
      className={`fixed top-4 right-4 p-4 rounded-lg shadow-lg border-l-4 z-50 transform transition-all duration-300 ${
        type === "success"
          ? "bg-green-50 border-green-500 text-green-800"
          : "bg-red-50 border-red-500 text-red-800"
      }`}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center">
          {type === "success" ? (
            <svg
              className="w-5 h-5 mr-2 text-green-500"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                clipRule="evenodd"
              />
            </svg>
          ) : (
            <svg
              className="w-5 h-5 mr-2 text-red-500"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                clipRule="evenodd"
              />
            </svg>
          )}
          <span className="font-medium">{message}</span>
        </div>
        <button
          onClick={onClose}
          className="ml-4 text-gray-400 hover:text-gray-600"
        >
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path
              fillRule="evenodd"
              d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
              clipRule="evenodd"
            />
          </svg>
        </button>
      </div>
    </div>
  );
}

// Interface for the toast state
interface ToastState {
  show: boolean;
  message: string;
  type: "success" | "error";
}

// Your Main Contact Component - Put this BELOW the Toast component
export function Contact() {
  const form = useRef<HTMLFormElement>(null);
  const [toast, setToast] = useState<ToastState>({
    show: false,
    message: "",
    type: "success",
  });

  const showToast = (
    message: string,
    type: "success" | "error" = "success"
  ) => {
    setToast({ show: true, message, type });
    setTimeout(
      () => setToast({ show: false, message: "", type: "success" }),
      5000
    );
  };

  const sendEmail = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!form.current) return;

    emailjs
      .sendForm(
        "service_x54n94d", // Replace with your EmailJS service ID
        "template_ut4akqh", // Replace with your EmailJS template ID
        form.current,
        "KsOSEyYnpuq_LNz3_" // Replace with your EmailJS public key
      )
      .then(
        (result) => {
          console.log(result.text);
          showToast(
            "🎉 Message sent successfully! We'll get back to you soon.",
            "success"
          );
          form.current?.reset();
        },
        (error) => {
          console.log(error.text);
          showToast(
            "😔 Failed to send message. Please try again or contact us directly.",
            "error"
          );
        }
      );
  };

  return (
    <>
      {/* Toast will appear here when triggered */}
      {toast.show && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() =>
            setToast({ show: false, message: "", type: "success" })
          }
        />
      )}

      <section id="contact" className="px-6 py-16 max-w-6xl mx-auto">
        <h2 className="text-3xl font-bold mb-4 text-center">Get In Touch</h2>

        <div className="flex flex-col md:flex-row gap-10">
          {/* Left: Form */}
          <div className="w-full md:w-1/2">
            <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-md">
              <h4 className="text-xl font-semibold mb-2">Send us a message</h4>
              <p className="mb-4 text-gray-600 dark:text-gray-400">
                Drop us a line! We are here to answer your questions 24/7.
              </p>

              <form ref={form} onSubmit={sendEmail} className="space-y-4">
                <input
                  type="text"
                  name="from_name"
                  className="w-full px-4 py-2 border rounded-md dark:bg-gray-700 dark:text-white"
                  placeholder="Your Name"
                  required
                />
                <input
                  type="email"
                  name="from_email"
                  className="w-full px-4 py-2 border rounded-md dark:bg-gray-700 dark:text-white"
                  placeholder="Your Email"
                  required
                />
                <input
                  type="text"
                  name="subject"
                  className="w-full px-4 py-2 border rounded-md dark:bg-gray-700 dark:text-white"
                  placeholder="Subject"
                  required
                />
                <textarea
                  name="message"
                  rows={5}
                  className="w-full px-4 py-2 border rounded-md dark:bg-gray-700 dark:text-white"
                  placeholder="Message"
                  required
                ></textarea>

                <div className="text-center">
                  <button
                    type="submit"
                    className="bg-blue-600 text-white px-6 py-2 rounded-full font-semibold hover:bg-blue-700 transition"
                  >
                    Send Message
                  </button>
                </div>
              </form>
            </div>
          </div>

          {/* Right: Contact Info */}
          <div className="w-full md:w-1/2 space-y-6 text-gray-700 dark:text-gray-300">
            <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-md">
              <h4 className="text-xl font-semibold mb-2">Visit Our Office</h4>
              <p>
                Diamond Plaza <br />
                4th Floor
                <br />
                Parklands
                <br />
                Nairobi - Kenya
                <br />
                <strong>Phone:</strong> +(254) 799 114011
                <br />
                <strong>Email:</strong> sales@ethostechhub.com
                <br />
              </p>
            </div>

            {/* <div className="flex gap-4" >
              <a href="#" className="text-blue-500 hover:text-blue-700 text-xl">
                <i className="fa fa-twitter"></i>
              </a>
              <a href="#" className="text-blue-800 hover:text-blue-900 text-xl">
                <i className="fa fa-facebook"></i>
              </a>
              <a href="#" className="text-pink-500 hover:text-pink-700 text-xl">
                <i className="fa fa-instagram"></i>
              </a>
              <a href="#" className="text-blue-600 hover:text-blue-800 text-xl">
                <i className="fa fa-linkedin"></i>
              </a>
            </div> */}

            <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-md">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3988.8567678068625!2d36.81612047602403!3d-1.257929498730098!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x182f17f9d0d2d191%3A0x5555e15cd94e20bf!2sEthos%20Tech%20Hub!5e0!3m2!1sen!2ske!4v1747037784081!5m2!1sen!2ske"
                width="100%"
                height="200"
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Office Location Map"
              ></iframe>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
